import { ImageConfiguration } from '@features/lazy-image/components/lazy-picture/lazy-picture.component';

/**
 * Hard-coded snapshot of the Contentful-driven navigation menus (2026-07-13).
 * Marketing pages are served by Webflow; this navigation stays frozen until it
 * is removed together with the marketing components (FOL-872).
 */
export interface NavigationMenuItem {
    id: string;
    label: string;
    url: string;
    flagIconConfig: ImageConfiguration;
}

export type NavigationMenu = Record<string, NavigationMenuItem[]>;

export interface SidebarMenuItem {
    id: string;
    label: string;
    url: string;
}

export const SIDEBAR_MENU: SidebarMenuItem[] = [
    { id: '5P4wtOQF1TIULKEixOOesS', label: 'Home', url: '/' },
    { id: '16sM2rE31x4DU97nAemRo6', label: 'My profile', url: '/my-profile' },
    {
        id: '6inuK6I9unGaoD8rEYMeD5',
        label: 'Work with us',
        url: '/work-with-us',
    },
    {
        id: '2lGPaudnP7edKWzdwIO5xT',
        label: 'Terms and conditions',
        url: '/terms-and-conditions',
    },
    {
        id: '3lsoPwO4KQiflEjUCnn46g',
        label: 'Privacy policy',
        url: '/privacy-policy',
    },
    {
        id: '4ENyxbD09poVP4unWYHZey',
        label: 'Cookies policy',
        url: '/cookies-policy',
    },
];

function navigationFlag(src: string, alt: string): ImageConfiguration {
    return { src, alt };
}
const TANZANIA_FLAG = navigationFlag(
    '/assets/images/navigation/flags/tanzania.svg',
    'Tanzania Flag'
);
const NEPAL_FLAG = navigationFlag(
    '/assets/images/navigation/flags/nepal.svg',
    'Nepal Flag'
);
const KENYA_FLAG = navigationFlag(
    '/assets/images/navigation/flags/kenya.svg',
    'Kenya Flag'
);
const PERU_FLAG = navigationFlag(
    '/assets/images/navigation/flags/peru.png',
    'Flag of Peru'
);
const UGANDA_FLAG = navigationFlag(
    '/assets/images/navigation/flags/uganda.svg',
    'Uganda Flag'
);
const RWANDA_FLAG = navigationFlag(
    '/assets/images/navigation/flags/rwanda.svg',
    'Rwanda Flag'
);
const BHUTAN_FLAG = navigationFlag(
    '/assets/images/navigation/flags/bhutan.svg',
    'Bhutan Flag'
);
const SRI_LANKA_FLAG = navigationFlag(
    '/assets/images/navigation/flags/sri-lanka.svg',
    'Sri Lanka Flag'
);
const ICELAND_FLAG = navigationFlag(
    '/assets/images/navigation/flags/iceland.svg',
    'Iceland Flag'
);
const SWEDEN_FLAG = navigationFlag(
    '/assets/images/navigation/flags/sweden.svg',
    'Sweden Flag'
);

export const TRIPS_MENU: NavigationMenu = {
    Africa: [
        {
            id: '7tMc0am53vJ4vU6OLgGAie',
            label: 'Climb Kilimanjaro',
            url: '/adventure-trips/climb-kilimanjaro',
            flagIconConfig: TANZANIA_FLAG,
        },
        {
            id: '4QoWiJ1JTdskgHQPz7WUcn',
            label: 'Tanzania Safari',
            url: '/adventure-trips/tanzania-safari',
            flagIconConfig: TANZANIA_FLAG,
        },
        {
            id: '2Oewk8hwcalG3mLOZNaNMi',
            label: 'Gorilla Trekking Uganda',
            url: '/adventure-trips/gorilla-trekking',
            flagIconConfig: UGANDA_FLAG,
        },
        {
            id: '5PHxtOZAV0u18Y7LHZb1ql',
            label: 'Gorilla Trekking Rwanda',
            url: '/adventure-trips/gorilla-trekking-rwanda',
            flagIconConfig: RWANDA_FLAG,
        },
        {
            id: 'zzhEWBfQjOKoGFOwjhX9b',
            label: 'Kenya Safari',
            url: '/adventure-trips/kenya-safari',
            flagIconConfig: KENYA_FLAG,
        },
        {
            id: '3XB8EZGVHtwCe8h52OEfva',
            label: 'Climb Meru',
            url: '/adventure-trips/climb-meru',
            flagIconConfig: TANZANIA_FLAG,
        },
        {
            id: '5jXWVGrAksF34V1gr4QLur',
            label: 'Zanzibar Beach Holidays',
            url: '/adventure-trips/zanzibar-beach-holidays',
            flagIconConfig: TANZANIA_FLAG,
        },
    ],
    Asia: [
        {
            id: '6Gyw4xYfGxbXGJMi8RnGKW',
            label: 'Nepal Peak Climbing',
            url: '/adventure-trips/nepal-peak-climbing',
            flagIconConfig: NEPAL_FLAG,
        },
        {
            id: '4xLUBceaxaSmqkrsZ1Kj8y',
            label: 'Luxury Nepal Adventures',
            url: '/adventure-trips/luxury-nepal-adventures',
            flagIconConfig: NEPAL_FLAG,
        },
        {
            id: '5ZmVk76XN2ZZ0KpAVf3MaU',
            label: 'Everest Base Camp Trek',
            url: '/adventure-trips/everest-base-camp',
            flagIconConfig: NEPAL_FLAG,
        },
        {
            id: '74essbwGzMS1yr71Id8Epw',
            label: 'Annapurna Circuit',
            url: '/adventure-trips/annapurna-circuit',
            flagIconConfig: NEPAL_FLAG,
        },
        {
            id: '5xXWmCPMK1STu3HPhT3vAX',
            label: 'Budget Nepal Adventures',
            url: '/adventure-trips/budget-nepal-adventures',
            flagIconConfig: NEPAL_FLAG,
        },
        {
            id: '3c2EWzsEZ6rbtDUHYhnkfl',
            label: 'Discover Bhutan',
            url: '/adventure-trips/discover-bhutan',
            flagIconConfig: BHUTAN_FLAG,
        },
        {
            id: '1xk0FhA6LUalshxH5IQXbR',
            label: 'Manaslu Circuit',
            url: '/adventure-trips/manaslu-circuit',
            flagIconConfig: NEPAL_FLAG,
        },
        {
            id: '6v2GDKknD9Tr3LyqcWCdpn',
            label: 'Explore Sri Lanka',
            url: '/adventure-trips/explore-sri-lanka',
            flagIconConfig: SRI_LANKA_FLAG,
        },
    ],
    'South America': [
        {
            id: '6DwfVZecxdEfccHCKKPVw4',
            label: 'Machu Picchu Trekking',
            url: '/adventure-trips/machu-picchu-trekking',
            flagIconConfig: PERU_FLAG,
        },
        {
            id: '4WYLLZZdmzF9drGMQaxv2D',
            label: 'Explore Peru',
            url: '/adventure-trips/explore-peru',
            flagIconConfig: PERU_FLAG,
        },
    ],
    Europe: [
        {
            id: '5WkLdDN4rLb0YK4bfZ64Ov',
            label: 'Wonders of Iceland',
            url: '/adventure-trips/wonders-of-iceland',
            flagIconConfig: ICELAND_FLAG,
        },
        {
            id: '7nD7OCdcIMIjS8j5NYfU25',
            label: 'Kayaking Sweden',
            url: '/adventure-trips/kayaking-sweden',
            flagIconConfig: SWEDEN_FLAG,
        },
    ],
};

export const TRAVEL_BLOG_MENU: NavigationMenu = {
    Destinations: [
        {
            id: '2z9aFa4sRe3wGzwt7e5D2v',
            label: 'Kilimanjaro Trekking',
            url: '/adventure-trips/climb-kilimanjaro#Knowledge',
            flagIconConfig: TANZANIA_FLAG,
        },
        {
            id: '6SHVEa37mgg7OOGTiBoitz',
            label: 'Mount Meru climb',
            url: '/adventure-trips/climb-meru#Knowledge',
            flagIconConfig: TANZANIA_FLAG,
        },
        {
            id: '6UFSOxbfHQHBCFy04KsGmq',
            label: 'Tanzania safari',
            url: '/adventure-trips/tanzania-safari#Knowledge',
            flagIconConfig: TANZANIA_FLAG,
        },
        {
            id: '4POoNn1IUIIw5aCUjJHdrl',
            label: 'Zanzibar',
            url: '/adventure-trips/zanzibar-beach-holidays#Knowledge',
            flagIconConfig: TANZANIA_FLAG,
        },
        {
            id: '2wiIZ6A6wqtd6q82BHjkTR',
            label: 'Kenya safari',
            url: '/adventure-trips/kenya-safari#Knowledge',
            flagIconConfig: KENYA_FLAG,
        },
        {
            id: '449zcpitJRPouwvNEwBVwb',
            label: 'Everest Base Camp trek',
            url: '/adventure-trips/everest-base-camp#Knowledge',
            flagIconConfig: NEPAL_FLAG,
        },
        {
            id: '3uePGqDSEU9rWYQLP3Jiez',
            label: 'Annapurna Circuit trek',
            url: '/adventure-trips/annapurna-circuit#Knowledge',
            flagIconConfig: NEPAL_FLAG,
        },
        {
            id: 'BJnCJfS2lhqXmXTsRzJ2q',
            label: 'Machu Picchu trekking',
            url: '/adventure-trips/machu-picchu-trekking#Knowledge',
            flagIconConfig: PERU_FLAG,
        },
        {
            id: '3pI7Q0KB0K3NdiUf0BFhA8',
            label: 'Explore Peru',
            url: '/adventure-trips/explore-peru#Knowledge',
            flagIconConfig: PERU_FLAG,
        },
        {
            id: '4ThboBrvDWq5rLdqqeV3mQ',
            label: 'Gorilla trekking Uganda',
            url: '/adventure-trips/gorilla-trekking#Knowledge',
            flagIconConfig: UGANDA_FLAG,
        },
        {
            id: '69Cef5ly5dToF5AemHeEQq',
            label: 'Gorilla trekking Rwanda',
            url: '/adventure-trips/gorilla-trekking-rwanda#Knowledge',
            flagIconConfig: RWANDA_FLAG,
        },
        {
            id: '5Q7SOryZu6XnBoTUAsSJse',
            label: 'Bhutan',
            url: '/adventure-trips/discover-bhutan#Knowledge',
            flagIconConfig: BHUTAN_FLAG,
        },
        {
            id: '2enxzeBpGlo8j2fZqUifQv',
            label: 'Sri Lanka',
            url: '/adventure-trips/explore-sri-lanka#Knowledge',
            flagIconConfig: SRI_LANKA_FLAG,
        },
    ],
    General: [
        {
            id: 'MGgqwwuAfdh3bCvxGjykl',
            label: 'Nepal peak climbing',
            url: '/adventure-trips/nepal-peak-climbing#Knowledge',
            flagIconConfig: NEPAL_FLAG,
        },
        {
            id: '3pQjr2O4FY29ENlnYrQYMO',
            label: 'Manaslu Circuit',
            url: '/adventure-trips/manaslu-circuit#Knowledge',
            flagIconConfig: NEPAL_FLAG,
        },
        {
            id: '6irVkyFaP7qo12JeebeUpZ',
            label: 'Luxury Nepal Adventures',
            url: '/adventure-trips/luxury-nepal-adventures#Knowledge',
            flagIconConfig: NEPAL_FLAG,
        },
        {
            id: '2oxejIIvmYuixfexZdptuV',
            label: 'Budget Nepal Adventures',
            url: '/adventure-trips/budget-nepal-adventures#Knowledge',
            flagIconConfig: NEPAL_FLAG,
        },
    ],
};
